import {
  getLeoUuid,
  LeoUUID,
  getInt32,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SpocUserDetails, SpocUserDetailsDTO } from "../types/spocUserDetails";
/* eslint-disable import/export */
export abstract class GetRespondentSpocsRPC implements
  LeoRPC<GetRespondentSpocsRPC.Request, GetRespondentSpocsRPC.Response, GetRespondentSpocsRPC.Errors.Errors>
{
  abstract execute(
    request: GetRespondentSpocsRPC.Request
  ): Promise<LeoRPCResult<GetRespondentSpocsRPC.Response, GetRespondentSpocsRPC.Errors.Errors>>;
}

export namespace GetRespondentSpocsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    respondentId: number;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetRespondentSpocsRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly respondentId: number;
  
  
    constructor(
    projectId: LeoUUID, 
    respondentId: number
    ) {
      this.projectId = projectId;
      this.respondentId = respondentId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const respondentId = getInt32(dto, "respondentId", InvalidRequestRespondentIdError);
      return new Request(
        projectId,
        respondentId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          respondentId: this.respondentId
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetRespondentSpocsRPC(projectId='${this.projectId}',respondentId='${this.respondentId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    spocUserDetails: SpocUserDetailsDTO[];
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetRespondentSpocsResponse: boolean = true;
  
    readonly spocUserDetails: SpocUserDetails[];
  
  
    constructor(
    spocUserDetails: SpocUserDetails[]
    ) {
      this.spocUserDetails = spocUserDetails;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const spocUserDetails = getList<SpocUserDetailsDTO, SpocUserDetails>(
        dto,
        "spocUserDetails",
        SpocUserDetails.fromDTO,
        InvalidResponseSpocUserDetailsError
      );
      return new Response(
        spocUserDetails
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          spocUserDetails: this.spocUserDetails.map((e: SpocUserDetails) => { return e.toDTO() })
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetRespondentSpocs(spocUserDetails='${this.spocUserDetails}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidRespondentId = "INVALID_RESPONDENT_ID"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetRespondentSpocsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidRespondentIdDTO {
      code: string;
    }
    
    export class InvalidRespondentId extends LeoRPCError<RPCError> {
      readonly isInvalidRespondentId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidRespondentId;
        super(code);
      }
    
    
      toDTO(): InvalidRespondentIdDTO {
        const dto: InvalidRespondentIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidRespondentId.prototype.toString = function toString(): string {
      return `GetRespondentSpocsRPC.INVALID_RESPONDENT_ID()`;
    };
    

    export type Errors = InvalidProjectId | InvalidRespondentId;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidRespondentIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidRespondentId:
          return new InvalidRespondentId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetRespondentSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestRespondentIdError extends GetRespondentSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRespondentIdError";
  }
}

export class InvalidResponseSpocUserDetailsError extends GetRespondentSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSpocUserDetailsError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetRespondentSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidRespondentIdError extends GetRespondentSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidRespondentIdError";
  }
}

